import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Link as MuiLink
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Paid as PaidIcon,
  Approval as ApprovalIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { expenseService } from '../../services/expenseService';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
dayjs.locale('es');

const ExpenseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [expense, setExpense] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);

  useEffect(() => {
    loadExpense();
  }, [id]);

  const loadExpense = async () => {
    try {
      setLoading(true);
      const response = await expenseService.getById(parseInt(id!));
      setExpense(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar el gasto');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await expenseService.approve(parseInt(id!));
      await loadExpense();
      setApproveDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al aprobar el gasto');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      setActionLoading(true);
      await expenseService.markAsPaid(parseInt(id!));
      await loadExpense();
      setMarkPaidDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al marcar como pagado');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await expenseService.delete(parseInt(id!));
      navigate('/expenses');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar el gasto');
    } finally {
      setActionLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'paid':
        return { label: 'Pagado', color: 'success' as const, icon: <PaidIcon /> };
      case 'approved':
        return { label: 'Aprobado', color: 'info' as const, icon: <ApprovalIcon /> };
      case 'pending':
        return { label: 'Pendiente', color: 'warning' as const, icon: <ErrorIcon /> };
      case 'cancelled':
        return { label: 'Cancelado', color: 'error' as const, icon: <WarningIcon /> };
      default:
        return { label: status, color: 'default' as const, icon: <ReceiptIcon /> };
    }
  };

  const formatCurrency = (amount: number, currency: string = 'RUB') => {
    const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '₽';
    return `${symbol} ${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !expense) {
    return (
      <Box m={2}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => navigate('/expenses')}>
            Volver a gastos
          </Button>
        }>
          {error || 'Gasto no encontrado'}
        </Alert>
      </Box>
    );
  }

  const statusInfo = getStatusInfo(expense.status);
  const isPending = expense.status === 'pending';
  const isApproved = expense.status === 'approved';
  const canApprove = isPending && ['admin', 'manager'].includes(user?.role || '');
  const canMarkPaid = (isPending || isApproved) && ['admin', 'manager'].includes(user?.role || '');

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <IconButton component={Link} to="/expenses" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'error.main' }}>
            <ReceiptLongIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {expense.concept}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {expense.supplier || 'Sin proveedor'}
            </Typography>
          </Box>
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            sx={{ ml: { xs: 0, sm: 2 } }}
          />
        </Box>
        
        <Box display="flex" flexWrap="wrap" gap={1}>
          <Tooltip title="Imprimir">
            <IconButton onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          
          {expense.receipt_url && (
            <Tooltip title="Ver comprobante">
              <IconButton onClick={() => setProofDialogOpen(true)}>
                <ReceiptIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {canApprove && (
            <Button
              variant="contained"
              color="info"
              onClick={() => setApproveDialogOpen(true)}
              disabled={actionLoading}
            >
              Aprobar Gasto
            </Button>
          )}
          
          {canMarkPaid && (
            <Button
              variant="contained"
              color="success"
              onClick={() => setMarkPaidDialogOpen(true)}
              disabled={actionLoading}
            >
              Marcar como Pagado
            </Button>
          )}
          
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/expenses/${id}/edit`)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Editar
          </Button>
          
          <Button
            variant="contained"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Eliminar
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Información General */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información General
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        Concepto
                      </TableCell>
                      <TableCell>{expense.concept}</TableCell>
                    </TableRow>
                    {expense.description && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Descripción
                        </TableCell>
                        <TableCell>{expense.description}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Categoría
                      </TableCell>
                      <TableCell>
                        <Chip label={expense.category || 'Sin categoría'} size="small" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Proveedor
                      </TableCell>
                      <TableCell>{expense.supplier || 'No especificado'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Detalles Financieros */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detalles Financieros
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" sx={{ width: '40%', bgcolor: 'action.hover' }}>
                        Monto
                      </TableCell>
                      <TableCell>
                        <Typography variant="h5" color="error.main" fontWeight="bold">
                          {formatCurrency(expense.amount, expense.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Fecha del Gasto
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          {dayjs(expense.expense_date).format('dddd, D [de] MMMM [de] YYYY')}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                        Método de Pago
                      </TableCell>
                      <TableCell>
                        {expense.payment_method === 'cash' && 'Efectivo'}
                        {expense.payment_method === 'bank_transfer' && 'Transferencia Bancaria'}
                        {expense.payment_method === 'card' && 'Tarjeta'}
                        {expense.payment_method === 'other' && 'Otro'}
                        {!expense.payment_method && 'No especificado'}
                      </TableCell>
                    </TableRow>
                    {expense.invoice_number && (
                      <TableRow>
                        <TableCell component="th" sx={{ bgcolor: 'action.hover' }}>
                          Número de Factura
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {expense.invoice_number}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Comentarios */}
        {expense.comments && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comentarios
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body1">
                    {expense.comments}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Información de Seguimiento */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información de Seguimiento
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Creado por
                  </Typography>
                  <Typography>
                    {expense.creator?.name || 'Sistema'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(expense.created_at).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
                
                {expense.approved_by && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Aprobado por
                    </Typography>
                    <Typography>
                      {expense.approver?.name || 'Administrador'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(expense.approved_at).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                  </Grid>
                )}
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Última actualización
                  </Typography>
                  <Typography>
                    {dayjs(expense.updated_at).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para aprobar */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Aprobar Gasto</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro que desea aprobar este gasto?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Concepto:</strong> {expense.concept}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> {formatCurrency(expense.amount, expense.currency)}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleApprove} color="info" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Aprobar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para marcar como pagado */}
      <Dialog open={markPaidDialogOpen} onClose={() => setMarkPaidDialogOpen(false)}>
        <DialogTitle>Marcar como Pagado</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro que desea marcar este gasto como pagado?
          </Typography>
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Concepto:</strong> {expense.concept}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> {formatCurrency(expense.amount, expense.currency)}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleMarkAsPaid} color="success" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Marcar como Pagado'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para eliminar */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro que desea eliminar este gasto?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Concepto:</strong> {expense.concept}
            </Typography>
            <Typography variant="body2">
              <strong>Monto:</strong> {formatCurrency(expense.amount, expense.currency)}
            </Typography>
          </Alert>
          {expense.status === 'paid' && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Este gasto ya fue pagado y registrado en el balance. Al eliminarlo, se revertirá automáticamente.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para ver comprobante */}
      <Dialog open={proofDialogOpen} onClose={() => setProofDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Comprobante de Gasto</DialogTitle>
        <DialogContent>
          {expense?.receipt_url && (
            <Box sx={{ textAlign: 'center' }}>
              {expense.receipt_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <Box
                  component="img"
                  src={expense.receipt_url}
                  alt="Comprobante"
                  sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ height: '60vh' }}>
                  <iframe
                    src={expense.receipt_url}
                    title="Comprobante"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<DownloadIcon />} href={expense?.receipt_url} download>
            Descargar
          </Button>
          <Button onClick={() => setProofDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseDetail;